import React, { useState, useEffect } from 'react';
import { getBotById } from '../../services/botService';
import { toast } from 'react-hot-toast';
import styles from './BotMetrics.module.css';
import logger from '../../utils/logger';

interface BotMetricsProps {
  botId: string;
}

interface BotData {
  _id: string;
  name: string;
  status: string;
  lastTriggerAt?: string;
  metrics?: {
    numberOfRuns: number;
    numberOfLeads: number;
  };
  totalSessions?: number;
  activeSessionsCount?: number;
  sessions?: Array<{
    _id: string;
    sessionId: string;
    whatsappNumber: string;
    chatbot: string;
    currentGroup: string;
    currentStep: number;
    responses: any;
    chatHistories: any[];
    isBotEnded: boolean;
    lastMessageTime: string;
    error?: {
      message: string;
      code: number;
      timestamp: string;
    } | null;
    lead: string | {
      _id: string;
      name?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    };
  }>;
}

const BotMetrics: React.FC<BotMetricsProps> = ({ botId }) => {
  logger.log('📊 BotMetrics: Rendering ENHANCED VERSION with session history for botId:', botId);

  const [botData, setBotData] = useState<BotData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastFetchedBotId, setLastFetchedBotId] = useState<string | null>(null);

  // 🔥 MAIN FIX: Only fetch when botId actually changes
  useEffect(() => {
    const fetchBotData = async () => {
      if (!botId || botId === lastFetchedBotId) {
        logger.log('🚫 BotMetrics: Skipping fetch - same botId or empty:', { botId, lastFetchedBotId });
        if (botId && !loading) {
          return;
        }
        return;
      }

      logger.log('🔄 BotMetrics: Fetching bot data for NEW ID:', botId);
      setLoading(true);

      try {
        const response = await getBotById(botId);
        const fetchedBotData = response.data;
        
        if (!fetchedBotData) {
          throw new Error('Bot not found');
        }
        
        logger.log('✅ BotMetrics: Bot data fetched successfully:', {
          id: fetchedBotData._id,
          name: fetchedBotData.name,
          status: fetchedBotData.status,
          sessionsCount: fetchedBotData.sessions?.length || 0
        });
        
        // 🔥 NEW: Console log session data with lead info
        if (fetchedBotData.sessions) {
          console.log('📊 Sessions with lead data:', fetchedBotData.sessions.map((s: { sessionId: any; lead: { name: any; }; }) => ({
            sessionId: s.sessionId,
            leadName: typeof s.lead === 'object' ? s.lead.name : 'No lead data'
          })));
        }
        
        setBotData(fetchedBotData);
        setLastFetchedBotId(botId);

      } catch (error) {
        console.error('❌ BotMetrics: Error fetching bot data:', error);
        toast.error('Failed to load bot metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchBotData();
  }, [botId]);

  console.log('🎯 BotMetrics RENDER:', { botId, lastFetchedBotId, loading, hasBotData: !!botData });

  // Loading state
  if (loading) {
    return (
      <div className={styles.metricsContainer}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '400px',
          color: '#6b7280',
          fontFamily: 'Gilroy, sans-serif'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #3FAEFD',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p>Loading bot metrics...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (!botData) {
    return (
      <div className={styles.metricsContainer}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '400px',
          color: '#ef4444',
          fontFamily: 'Gilroy, sans-serif'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3>Failed to Load Metrics</h3>
          <p>Could not fetch bot data. Please try again.</p>
        </div>
      </div>
    );
  }

  // Calculate advanced metrics
  const totalRuns = botData.metrics?.numberOfRuns || 0;
  const totalSessions = botData.totalSessions || 0;
  const activeSessions = botData.activeSessionsCount || 0;
  const erroredSessions = botData.sessions?.filter(s => s.error)?.length || 0;
  const completedSessions = botData.sessions?.filter(s => s.isBotEnded && !s.error)?.length || 0;
  const totalLeads = botData.metrics?.numberOfLeads || 0;
  
  // Advanced calculations
  const successRate = totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0;
  const errorRate = totalSessions > 0 ? Math.round((erroredSessions / totalSessions) * 100) : 0;
  const conversionRate = totalRuns > 0 ? Math.round((totalLeads / totalRuns) * 100) : 0;
  const activeRate = totalSessions > 0 ? Math.round((activeSessions / totalSessions) * 100) : 0;

  // Format date with enhanced styling
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never triggered';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.round(diffInHours)}h ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // 🔥 NEW: Format session date specifically
  const formatSessionDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // 🔥 NEW: Format WhatsApp number for display
  const formatPhoneNumber = (number: string) => {
    // Remove country code if present and format
    const cleaned = number.replace(/^\+?91/, '');
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    return number;
  };

  // 🔥 NEW: Get lead name from session data
  const getLeadName = (session: any) => {
    console.log('👤 Getting lead name for session:', session.sessionId, 'Lead data:', session.lead);
    
    if (typeof session.lead === 'object' && session.lead?.name) {
      return session.lead.name;
    }
    
    // Fallback: try firstName + lastName
    if (typeof session.lead === 'object' && (session.lead?.firstName || session.lead?.lastName)) {
      const firstName = session.lead.firstName || '';
      const lastName = session.lead.lastName || '';
      return `${firstName} ${lastName}`.trim();
    }
    
    return 'Unknown Lead';
  };

  // Get recent errors with enhanced details
  const recentErrors = botData.sessions
    ?.filter(s => s.error)
    ?.slice(0, 3)
    ?.map(s => ({
      message: s.error!.message,
      time: formatDate(s.error!.timestamp),
      code: s.error!.code,
      severity: s.error!.code >= 500 ? 'high' : s.error!.code >= 400 ? 'medium' : 'low'
    })) || [];

  // Determine overall health status
  const getHealthStatus = () => {
    if (errorRate === 0 && successRate > 80) return { status: 'excellent', color: '#10b981', icon: '🟢' };
    if (errorRate < 20 && successRate > 60) return { status: 'good', color: '#3FAEFD', icon: '🔵' };
    if (errorRate < 50) return { status: 'warning', color: '#f59e0b', icon: '🟡' };
    return { status: 'critical', color: '#ef4444', icon: '🔴' };
  };

  const healthStatus = getHealthStatus();

  return (
    <div className={styles.metricsContainer}>
      <div className={styles.metricsHeader}>
        <div>
          <h3>Bot Performance Analytics</h3>
          <span style={{ color: healthStatus.color, fontSize: '16px', fontWeight: '700' }}>
            {healthStatus.icon} Health: {healthStatus.status.toUpperCase()}
          </span>
        </div>
        
        <span className={styles.lastTrigger}>
          ⚡ {formatDate(botData.lastTriggerAt)}
        </span>
      </div>

      {/* Enhanced Metrics Cards */}
      <div className={styles.metricsGrid}>
        
        {/* Total Executions */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{totalRuns}</div>
          <div className={styles.metricLabel}>Total Executions</div>
          <div className={styles.metricIcon}>🚀</div>
        </div>

        {/* Success Rate with Dynamic Color */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{successRate}%</div>
          <div className={styles.metricLabel}>Success Rate</div>
          <div className={styles.metricIcon}>✨</div>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ 
                width: `${successRate}%`, 
                background: `linear-gradient(90deg, ${
                  successRate > 80 ? '#10b981' : 
                  successRate > 60 ? '#3FAEFD' : 
                  successRate > 40 ? '#f59e0b' : '#ef4444'
                } 0%, ${
                  successRate > 80 ? '#34d399' : 
                  successRate > 60 ? '#60a5fa' : 
                  successRate > 40 ? '#fbbf24' : '#f87171'
                } 100%)`
              }}
            ></div>
          </div>
        </div>

        {/* Chat Sessions */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{totalSessions}</div>
          <div className={styles.metricLabel}>Chat Sessions</div>
          <div className={styles.metricIcon}>💬</div>
        </div>

        {/* Active Sessions with Pulse */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{activeSessions}</div>
          <div className={styles.metricLabel}>Live Sessions</div>
          <div className={styles.metricIcon}>🔄</div>
          {activeSessions > 0 && (
            <div className={styles.statusBadge}>
              <span className={styles.activeIndicator}></span> 
              {activeSessions} Active
            </div>
          )}
        </div>

        {/* Leads Generated */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{totalLeads}</div>
          <div className={styles.metricLabel}>Leads Generated</div>
          <div className={styles.metricIcon}>👥</div>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ 
                width: `${conversionRate}%`, 
                background: 'linear-gradient(90deg, #8b5cf6 0%, #a78bfa 100%)'
              }}
            ></div>
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', fontWeight: '600' }}>
            {conversionRate}% conversion rate
          </div>
        </div>

        {/* Error Rate */}
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>{errorRate}%</div>
          <div className={styles.metricLabel}>Error Rate</div>
          <div className={styles.metricIcon}>⚠️</div>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ 
                width: `${errorRate}%`, 
                background: 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)'
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Enhanced Session Status Breakdown */}
      <div className={styles.sessionBreakdown}>
        <h4>📈 Session Analytics</h4>
        <div className={styles.statusBars}>
          
          {/* Completed Sessions */}
          <div className={styles.statusBar}>
            <div className={styles.statusLabel}>
              <span className={styles.statusDot} style={{ backgroundColor: '#10b981' }}></span>
              Completed Sessions ({completedSessions})
            </div>
            <div className={styles.statusProgress}>
              <div 
                className={styles.statusFill} 
                style={{ 
                  width: totalSessions > 0 ? `${(completedSessions / totalSessions) * 100}%` : '0%',
                  background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
                }}
              ></div>
            </div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#10b981', minWidth: '60px' }}>
              {totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0}%
            </div>
          </div>

          {/* Active Sessions */}
          <div className={styles.statusBar}>
            <div className={styles.statusLabel}>
              <span className={styles.statusDot} style={{ backgroundColor: '#3FAEFD' }}></span>
              Active Sessions ({activeSessions})
            </div>
            <div className={styles.statusProgress}>
              <div 
                className={styles.statusFill} 
                style={{ 
                  width: totalSessions > 0 ? `${(activeSessions / totalSessions) * 100}%` : '0%',
                  background: 'linear-gradient(90deg, #3FAEFD 0%, #60a5fa 100%)'
                }}
              ></div>
            </div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#3FAEFD', minWidth: '60px' }}>
              {activeRate}%
            </div>
          </div>

          {/* Errored Sessions */}
          <div className={styles.statusBar}>
            <div className={styles.statusLabel}>
              <span className={styles.statusDot} style={{ backgroundColor: '#ef4444' }}></span>
              Failed Sessions ({erroredSessions})
            </div>
            <div className={styles.statusProgress}>
              <div 
                className={styles.statusFill} 
                style={{ 
                  width: totalSessions > 0 ? `${(erroredSessions / totalSessions) * 100}%` : '0%',
                  background: 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)'
                }}
              ></div>
            </div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#ef4444', minWidth: '60px' }}>
              {errorRate}%
            </div>
          </div>
        </div>
      </div>

      {/* 🔥 SIMPLE: Session History with Lead Names + Scrollable */}
      {botData.sessions && botData.sessions.length > 0 && (
        <div className={styles.sessionBreakdown}>
          <h4>💬 Session History ({botData.sessions.length} Sessions)</h4>
          
          {/* 🔥 SCROLLABLE: Max height container */}
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px',
            background: 'white'
          }}>
            {botData.sessions.map((session, index) => (
              <div key={session._id} style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '12px'
              }}>
                {/* Session Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <h5 style={{ margin: 0, color: '#1f2937' }}>
                    💬 Session #{index + 1}
                  </h5>
                  
                  {session.isBotEnded ? (
                    <span style={{
                      background: '#10b981',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      ✅ Completed
                    </span>
                  ) : (
                    <span style={{
                      background: '#3FAEFD',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      🔄 Active
                    </span>
                  )}
                </div>
                
                {/* Session Details - Simple Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px'
                }}>
                  
                  {/* 🔥 LEAD NAME - First and prominent */}
                  <div style={{
                    background: 'rgba(63, 174, 253, 0.1)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(63, 174, 253, 0.2)'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>
                      👤 Lead Name
                    </div>
                    <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '700' }}>
                      {getLeadName(session)}
                    </div>
                  </div>
                  
                  {/* WhatsApp Number */}
                  <div style={{
                    background: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>
                      📱 Phone
                    </div>
                    <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '600' }}>
                      {formatPhoneNumber(session.whatsappNumber)}
                    </div>
                  </div>
                  
                  {/* Last Message Time */}
                  <div style={{
                    background: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>
                      ⏰ Last Message
                    </div>
                    <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '600' }}>
                      {formatSessionDate(session.lastMessageTime)}
                    </div>
                  </div>
                </div>
                
                {/* Error Display */}
                {session.error && (
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    borderRadius: '6px',
                    padding: '12px',
                    marginTop: '12px'
                  }}>
                    <div style={{ color: '#ef4444', fontSize: '14px', fontWeight: '600' }}>
                      ❌ Error {session.error.code}: {session.error.message}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enhanced Recent Errors */}
      {recentErrors.length > 0 && (
        <div className={styles.errorsSection}>
          <h4>🚨 Recent Issues</h4>
          <div className={styles.errorsList}>
            {recentErrors.map((error, index) => (
              <div key={index} className={styles.errorItem}>
                <div className={styles.errorMessage}>
                  <span 
                    className={styles.errorCode}
                    style={{
                      background: error.severity === 'high' ? '#ef4444' :
                                  error.severity === 'medium' ? '#f59e0b' : '#6b7280'
                    }}
                  >
                    {error.code}
                  </span>
                  {error.message}
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6b7280', 
                    marginTop: '6px',
                    fontWeight: '600'
                  }}>
                    Severity: {error.severity.toUpperCase()}
                  </div>
                </div>
                <div className={styles.errorTime}>{error.time}</div>
              </div>
            ))}
          </div>
          
          {erroredSessions > 3 && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: '16px', 
              fontSize: '14px', 
              color: '#6b7280',
              fontWeight: '600'
            }}>
              + {erroredSessions - 3} more errors in session history
            </div>
          )}
        </div>
      )}

      {/* Enhanced Bot Status */}
      <div className={styles.botStatus}>
        <div className={`${styles.statusIndicator} ${styles[`status-${botData.status}`]}`}>
          <span className={styles.statusDot}></span>
          Bot Status: {botData.status.toUpperCase()}
          <span style={{ fontSize: '12px', opacity: '0.8' }}>
            • Last updated {formatDate(botData.lastTriggerAt)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default BotMetrics;